import { shopifyGraphqlFetch } from "@/api/shopify-graphql/shopify-graphql"
import { ShopifyGraphqlFetchResponse } from "@/api/shopify-graphql/shopify-graphql.type"

export interface ThemeLayoutShopifyImageFile {
  id: string
  url: string
}

interface StagedTarget {
  url: string
  resourceUrl: string | null
  parameters: { name: string; value: string }[]
}

interface GraphqlUserError {
  field: string[] | null
  message: string
  code?: string
}

const STAGED_UPLOADS_CREATE = `
  mutation stagedUploadsCreate($input: [StagedUploadInput!]!) {
    stagedUploadsCreate(input: $input) {
      stagedTargets {
        url
        resourceUrl
        parameters {
          name
          value
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const FILE_CREATE = `
  mutation themeLayoutFileCreate($files: [FileCreateInput!]!) {
    fileCreate(files: $files) {
      files {
        __typename
        ... on MediaImage {
          id
          fileStatus
          image {
            url
            width
            height
          }
          preview {
            image {
              url
            }
          }
        }
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`

const FILE_DELETE = `
  mutation themeLayoutFileDelete($fileIds: [ID!]!) {
    fileDelete(fileIds: $fileIds) {
      deletedFileIds
      userErrors {
        field
        message
        code
      }
    }
  }
`

const NODE_MEDIA_IMAGE = `
  query themeLayoutNodeMediaImage($id: ID!) {
    node(id: $id) {
      __typename
      ... on MediaImage {
        id
        fileStatus
        image {
          url
          width
          height
        }
        preview {
          image {
            url
          }
        }
      }
    }
  }
`

const pickUserErrorMessage = (errors: GraphqlUserError[] | undefined): string => {
  if (!errors || errors.length === 0) {
    return ""
  }
  return errors.map((e) => e.message).join(" ")
}

const uploadBlobToStagedTarget = async (file: File, target: StagedTarget): Promise<void> => {
  const formData = new FormData()

  target.parameters.forEach((p) => {
    formData.append(p.name, p.value)
  })
  formData.append("file", file)

  const res = await fetch(target.url, {
    method: "POST",
    body: formData,
  })

  if (!res.ok) {
    const text = await res.text().catch(() => "")

    throw new Error(text || `Upload failed (${res.status})`)
  }
}

export const uploadThemeImageViaStagedAndFileCreate = async (
  file: File,
  safeFilename: string,
): Promise<ThemeLayoutShopifyImageFile> => {
  const stagedRes = await shopifyGraphqlFetch<{
    stagedUploadsCreate: {
      stagedTargets: StagedTarget[] | null
      userErrors: GraphqlUserError[]
    }
  }>(STAGED_UPLOADS_CREATE, {
    input: [
      {
        filename: safeFilename,
        mimeType: file.type || "image/jpeg",
        fileSize: String(file.size),
        httpMethod: "POST",
        resource: "IMAGE",
      },
    ],
  })

  if (stagedRes.errors && stagedRes.errors.length > 0) {
    throw new Error(stagedRes.errors.map((e) => e.message).join(" "))
  }

  const stagedPayload = stagedRes.data?.stagedUploadsCreate
  const errMsg = pickUserErrorMessage(stagedPayload?.userErrors)

  if (errMsg) {
    throw new Error(errMsg)
  }

  const target = stagedPayload?.stagedTargets?.[0]
  const resourceUrl = target?.resourceUrl || ""

  if (!target?.url || !resourceUrl) {
    throw new Error("Staged upload target missing")
  }

  await uploadBlobToStagedTarget(file, target)

  const createRes = await shopifyGraphqlFetch<{
    fileCreate: {
      files: {
        __typename: string
        id: string
        fileStatus: string
        image?: { url: string | null } | null
        preview?: { image: { url: string | null } | null } | null
      }[] | null
      userErrors: GraphqlUserError[]
    }
  }>(FILE_CREATE, {
    files: [
      {
        alt: safeFilename,
        contentType: "IMAGE",
        originalSource: resourceUrl,
        filename: safeFilename,
      },
    ],
  })

  if (createRes.errors && createRes.errors.length > 0) {
    throw new Error(createRes.errors.map((e) => e.message).join(" "))
  }

  const createPayload = createRes.data?.fileCreate
  const createErr = pickUserErrorMessage(createPayload?.userErrors)

  if (createErr) {
    throw new Error(createErr)
  }

  const created = createPayload?.files?.[0]

  if (!created?.id) {
    throw new Error("fileCreate returned no file")
  }

  const immediateUrl = created.image?.url || created.preview?.image?.url || ""

  if (immediateUrl) {
    return { id: created.id, url: immediateUrl }
  }

  return pollThemeLayoutMediaImageUrl(created.id)
}

const pollThemeLayoutMediaImageUrl = async (
  id: string,
  maxAttempts = 20,
  delayMs = 400,
): Promise<ThemeLayoutShopifyImageFile> => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const res = await shopifyGraphqlFetch<{
      node: {
        __typename: string
        id: string
        fileStatus: string
        image?: { url: string | null } | null
        preview?: { image: { url: string | null } | null } | null
      } | null
    }>(NODE_MEDIA_IMAGE, { id })

    if (res.errors && res.errors.length > 0) {
      throw new Error(res.errors.map((e) => e.message).join(" "))
    }

    const node = res.data?.node

    if (!node || node.__typename !== "MediaImage") {
      throw new Error("Media image not found")
    }

    const url =
      node.image?.url
      || node.preview?.image?.url
      || ""

    if (url) {
      return { id: node.id, url }
    }

    if (node.fileStatus === "FAILED") {
      throw new Error("Image processing failed")
    }

    await new Promise((r) => setTimeout(r, delayMs))
  }

  throw new Error("Timed out waiting for image URL")
}

export const deleteShopifyFilesByIds = async (fileIds: string[]): Promise<void> => {
  if (fileIds.length === 0) {
    return
  }

  const res: ShopifyGraphqlFetchResponse<{
    fileDelete: {
      deletedFileIds: string[] | null
      userErrors: GraphqlUserError[]
    }
  }> = await shopifyGraphqlFetch(FILE_DELETE, { fileIds })

  if (res.errors && res.errors.length > 0) {
    throw new Error(res.errors.map((e) => e.message).join(" "))
  }

  const payload = res.data?.fileDelete
  const err = pickUserErrorMessage(payload?.userErrors)

  if (err) {
    throw new Error(err)
  }
}

export interface ThemeLayoutFilesListItem {
  id: string
  label: string
  thumbnailUrl: string
  /** 为 false 时不展示删除（例如内置图，非 Shopify MediaImage） */
  deletable?: boolean
}

export const shopifyMediaImageNodeExists = async (id: string): Promise<boolean> => {
  const res = await shopifyGraphqlFetch<{
    node: { __typename: string } | null
  }>(NODE_MEDIA_IMAGE, { id })

  if (res.errors && res.errors.length > 0) {
    return true
  }

  const node = res.data?.node

  return Boolean(node && node.__typename === "MediaImage")
}

export const getThemeLayoutMediaImagePreviewUrl = async (id: string): Promise<string | null> => {
  const res = await shopifyGraphqlFetch<{
    node: {
      __typename: string
      image?: { url: string | null } | null
      preview?: { image: { url: string | null } | null } | null
    } | null
  }>(NODE_MEDIA_IMAGE, { id })

  if (res.errors && res.errors.length > 0) {
    return null
  }

  const node = res.data?.node

  if (!node || node.__typename !== "MediaImage") {
    return null
  }

  return node.image?.url || node.preview?.image?.url || null
}
